'use client'

import React, { createContext, useContext, useMemo } from 'react'

import type { IChatRuntimeConfig, INormalizedChatRuntimeConfig } from './types'

const defaultGetPathname = () => ''
const defaultGetSearchParam = () => null
const defaultIsEmbedRoute = () => false
const defaultIsInboxPath = () => false

const ChatRuntimeConfigContext = createContext<INormalizedChatRuntimeConfig | null>(null)

interface IChatRuntimeConfigProviderProps {
  children: React.ReactNode
  config: IChatRuntimeConfig
}

export const ChatRuntimeConfigProvider = ({ children, config }: IChatRuntimeConfigProviderProps) => {
  const value = useMemo<INormalizedChatRuntimeConfig>(
    () => ({
      ...config,
      activeChannelState: config.activeChannelState ?? {},
      getPathname: config.getPathname ?? defaultGetPathname,
      getSearchParam: config.getSearchParam ?? defaultGetSearchParam,
      isEmbedRoute: config.isEmbedRoute ?? defaultIsEmbedRoute,
      isInboxPath: config.isInboxPath ?? defaultIsInboxPath,
      mentionableUsers: config.mentionableUsers ?? [],
      resolveInboxView: config.resolveInboxView ?? (() => config.defaultInboxView ?? 'all'),
      isLoadingMembers: config.isLoadingMembers ?? false,
      teamMembers: config.teamMembers ?? [],
      telemetry: config.telemetry ?? {},
      workspaceUsers: config.workspaceUsers ?? [],
      workspaces: config.workspaces ?? []
    }),
    [config]
  )

  return <ChatRuntimeConfigContext.Provider value={value}>{children}</ChatRuntimeConfigContext.Provider>
}

export const useChatRuntimeConfig = () => {
  const context = useContext(ChatRuntimeConfigContext)

  if (!context) {
    throw new Error('useChatRuntimeConfig must be used within ChatRuntimeConfigProvider')
  }

  return context
}
